import { siteConfig } from "@acme/config";
import { createFileRoute } from "@tanstack/react-router";
import { Facts } from "~/components/inside/facts";
import { NewTab } from "~/components/inside/new-tab";
import { PageIntro, PageLabel } from "~/components/inside/page-intro";
import { QuietLink } from "~/components/inside/quiet-link";
import { usePageEngagement } from "~/hooks/use-page-engagement";
import { publicCacheHeaders } from "~/lib/cache-headers";
import { getPrinciples, getStudioFacts } from "~/lib/constants/about";
import { getCtaConfig } from "~/lib/constants/navbar";
import { seo } from "~/lib/seo";
import {
  generateStructuredDataGraph,
  getAboutPageSchemas,
} from "~/lib/structured-data";
import * as m from "~/paraglide/messages.js";

export const Route = createFileRoute("/(public)/about")({
  component: AboutPage,
  head: () => {
    const seoData = seo({
      description: m.meta_about_description(),
      keywords:
        "Lindevo, AI automation company Latvia, AI development company, AI consulting, AI systems engineering, Liepāja, small business automation",
      path: "/about",
      title: m.meta_about_title(),
    });
    const structuredData = generateStructuredDataGraph(getAboutPageSchemas());
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [
        {
          children: structuredData,
          type: "application/ld+json",
        },
      ],
    };
  },
  headers: publicCacheHeaders,
});

/**
 * Who is behind the systems.
 *
 * The only inner page that opens on its own headline rather than a scene band:
 * the page is the introduction, so it starts at the largest type in the system
 * and ends on an address rather than a panel.
 */
function AboutPage() {
  usePageEngagement({ pagePath: "/about" });

  return (
    <div className="wrap">
      <section className="about-hero">
        <PageLabel>{m.about_label()}</PageLabel>
        <h1 className="type-display">
          {m.about_title_1()} <br />
          {m.about_title_2()} <br />
          {m.about_title_3()}
        </h1>

        <div className="about-intro">
          <PageIntro size="lg">{m.about_intro()}</PageIntro>
          <p>{m.about_intro_body()}</p>
        </div>
      </section>

      <div className="brand-band">
        {/* The signature, not the nav wordmark: it goes nowhere and carries the
            studio name as its alt text. */}
        <div className="brand-signature">
          <img
            alt={siteConfig.name}
            height={92}
            src={siteConfig.logoDark}
            width={340}
          />
        </div>
        <div className="studio-card">
          <Facts rows={getStudioFacts()} />
        </div>
      </div>

      <section className="belief">
        <h2>
          {m.about_belief_title_1()} <br />
          {m.about_belief_title_2()}
        </h2>
        <div className="belief-copy">
          <p>{m.about_belief_body_1()}</p>
          <p>{m.about_belief_body_2()}</p>
          <p>{m.about_belief_body_3()}</p>
        </div>
      </section>

      <section className="principles">
        <div className="principles-head">
          <h2>{m.about_principles_title()}</h2>
          <p>{m.about_principles_note()}</p>
        </div>
        <div className="principle-grid">
          {getPrinciples().map((principle) => (
            <article key={principle.title}>
              <h3>{principle.title}</h3>
              <p>{principle.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-contact">
        <div>
          <h2>
            {m.about_contact_title_1()} <br />
            {m.about_contact_title_2()}
          </h2>
          <p>{m.about_contact_body()}</p>
          <QuietLink to="/how-we-work">{m.about_contact_cta()}</QuietLink>
        </div>

        <div className="contact-links">
          <a
            className="email-large"
            href={`mailto:${siteConfig.contacts.mail}`}
          >
            {siteConfig.contacts.mail}
          </a>
          <a
            className="button button-solid"
            href={getCtaConfig().href}
            rel="noopener"
            target="_blank"
          >
            {m.inner_cta_call()}
            <NewTab />
          </a>
        </div>
      </section>
    </div>
  );
}
